import React from 'react';

import { DateTime } from 'luxon';
import { Badge } from 'antd';
import {
  FileTextOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

import Colors from '../constants/Colors';
import BorderlessButton from '../common/buttons/BorderlessButton';

export const CERTIFICATION_NAME_KEY_MAP = {
  users: 'userName',
  vendors: 'vendor',
  equipment: 'equipment',
};

export const getCertificationEntityName = (certification, entityType) => (
  certification[CERTIFICATION_NAME_KEY_MAP[entityType] ?? 'name'] ?? ''
);

export default function getCertificationColumns({
  entityType,
  showUserName = false,
  getCanManage,
  onUpdateClick,
  onDeleteClick,
  onViewClick,
  customFieldColumns = [],
  onFormClick,
}) {
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Acquired Date',
      dataIndex: 'acquiredDate',
      key: 'acquiredDate',
      render: (text) => (
        text ? DateTime.fromSeconds(text).toLocaleString(DateTime.DATE_FULL) : ''
      ),
    },
    {
      title: 'Expiration Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        // For certifications without expiry dates, treat them as having a very far future date
        // If both expiryDate and acquiredDate are missing, sort them last
        const aExpiry = (!a.expiryDate && !a.acquiredDate) 
          ? Number.MAX_SAFE_INTEGER + 1
          : (a.expiryDate || Number.MAX_SAFE_INTEGER);
        const bExpiry = (!b.expiryDate && !b.acquiredDate)
          ? Number.MAX_SAFE_INTEGER + 1
          : (b.expiryDate || Number.MAX_SAFE_INTEGER);
        
        return aExpiry - bExpiry;
      },
      render: (text, record) => {
        const isExpired = DateTime.local().valueOf() > (record.expiryDate * 1000);
        return (
          <div className={isExpired ? 'user-expired-certification-label' : ''}>
            {text ? DateTime.fromSeconds(text).toLocaleString(DateTime.DATE_FULL) : ''}
          </div>
        );
      },
    },
    {
      title: 'Issuer',
      dataIndex: 'issuer',
      key: 'issuer',
    },
    {
      title: 'Files',
      dataIndex: 'files',
      key: 'files',
      render: (_, record) => (
        <Badge count={record.files.length} showZero>
          <FileOutlined style={{ fontSize: '25px' }} />
        </Badge>
      ),
    },
  ];

  if (customFieldColumns?.length) {
    columns.push(...customFieldColumns);
  }

  columns.push(
    {
      title: '',
      dataIndex: 'delete',
      key: 'delete',
      align: 'right',
      width: 10,
      render: (_, record) => {
        const canManage = getCanManage?.(record);

        return (
          <>
            {record.formId && (
              <BorderlessButton
                iconNode={<FileTextOutlined />}
                onClick={() => onFormClick(record.formId)}
              />
            )}
            {canManage && onUpdateClick && (
              <BorderlessButton
                iconNode={<EditOutlined />}
                onClick={() => onUpdateClick(record)}
              />
            )}
            {canManage && onDeleteClick && (
              <BorderlessButton
                iconNode={<DeleteOutlined />}
                color={Colors.ONTRACCR_RED}
                onClick={() => onDeleteClick(record)}
              />
            )}
            {onViewClick && (
              <BorderlessButton
                iconNode={<InfoCircleOutlined />}
                onClick={() => onViewClick(record)}
              />
            )}
          </>
        );
      },
    },
  );

  if (showUserName) {
    const key = CERTIFICATION_NAME_KEY_MAP[entityType] ?? 'name';
    columns.unshift({
      title: 'Name',
      dataIndex: key,
      key,
    });
  }

  return columns;
}
